import axios, { AxiosError } from "axios";
import {
  buildAdminLoginRedirectPath,
  isAdminAuthPath,
  isProtectedAdminPath,
} from "@/lib/auth-routing";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:5000";

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
  }
  return config;
});

type RetryableRequestConfig = NonNullable<AxiosError["config"]> & { _retry?: boolean };

function buildLoginRedirectUrl(): string {
  return buildAdminLoginRedirectPath(window.location.pathname, window.location.search);
}

function isAuthPageLocation(): boolean {
  return typeof window !== "undefined" && isAdminAuthPath(window.location.pathname);
}

function isProtectedPageLocation(): boolean {
  return typeof window !== "undefined" && isProtectedAdminPath(window.location.pathname);
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (error.response?.status === 401 && typeof window !== "undefined" && originalRequest) {
      const url = originalRequest.url ?? "";
      const isAuthEndpoint =
        url.includes("/api/auth/login") ||
        url.includes("/api/auth/refresh") ||
        url.includes("/api/auth/logout");
      const isOptionalSessionProbe = url.includes("/api/auth/me");

      if (!isProtectedPageLocation() || isOptionalSessionProbe) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;

        try {
          await api.post("/api/auth/refresh", {});
          return api(originalRequest);
        } catch {
          if (!isAuthPageLocation()) {
            window.sessionStorage.setItem(
              "auth_error",
              "Oturumunuz sona erdi. Lütfen tekrar giriş yapın."
            );
            window.location.href = buildLoginRedirectUrl();
          }
          return new Promise(() => {});
        }
      } else if (!isAuthEndpoint && !isAuthPageLocation()) {
        window.sessionStorage.setItem(
          "auth_error",
          "Oturumunuz sona erdi. Lütfen tekrar giriş yapın."
        );
        window.location.href = buildLoginRedirectUrl();
        return new Promise(() => {});
      }
    }

    return Promise.reject(error);
  }
);

export default api;
